import { useCallback, useEffect, useMemo } from "react";

import {
  selectConfiguredPrimaryColor,
  selectHostColorSchemePreference,
  selectIsThemeAccessible,
  selectSelectedTheme,
  setDarkAccessibleTheme,
  setLightAccessibleTheme,
  setSelectedTheme,
  setTheme,
} from "@/features/app/appSlice";
import { selectConfigItem } from "@/features/config/configSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  AccessibleColorThemes,
  ColorThemes,
  DARK_ACCESSIBLE_THEME,
  DEFAULT_THEME,
  LIGHT_ACCESSIBLE_THEME,
} from "@/features/app/constants";
import {
  setCSSColorVariables,
  toCustomColorTheme,
} from "@/features/app/hooks/theme/utils";
import { selectCurrentBuildingGeoJSON } from "@/features/cartography/cartographySlice.selectors";
import { getThemeMediaQueryList, resolveSystemTheme } from "./themeDetection";
import useBaseMapStyle from "./useBaseMapStyle";
import useFontStyle from "./useFontStyle";

function useTheme() {
  const dispatch = useAppDispatch();
  const buildingGeoJSON = useAppSelector(selectCurrentBuildingGeoJSON);
  const styleBorderTheme = useAppSelector(
    selectConfigItem("styles.theme.border_theme"),
  );
  const primaryColor = useAppSelector(selectConfiguredPrimaryColor);
  const colorTheme = useAppSelector(
    selectConfigItem("styles.theme.colorTheme"),
  );
  const themeFromConfigFile = useAppSelector(selectConfigItem("styles.theme"));
  const labelsConfig = useAppSelector(selectConfigItem("styles.map.labels"));
  const pathColor = useAppSelector(selectConfigItem("styles.path_color"));
  const selectedPoiOutlineColor = useAppSelector(
    selectConfigItem("styles.map.pois.outline.color_selected"),
  );
  const pathAnimationColor = useAppSelector(
    selectConfigItem("styles.path_animation_color"),
  );
  const pathUnselectedColor = useAppSelector(
    selectConfigItem("styles.path_unselected_color"),
  );
  const pathArrowColor = useAppSelector(
    selectConfigItem("styles.path_arrow_color"),
  );
  const showStepArrow = useAppSelector(
    selectConfigItem("styles.path.show_step_arrow"),
  );
  const pathDotSize = useAppSelector(selectConfigItem("styles.path.dots_size"));
  const pathDotGap = useAppSelector(selectConfigItem("styles.path.dots_gap"));
  const isThemeAccessible = useAppSelector(selectIsThemeAccessible);
  const hostColorSchemePreference = useAppSelector(
    selectHostColorSchemePreference,
  );
  const selectedTheme = useAppSelector(selectSelectedTheme);

  useFontStyle();
  useBaseMapStyle();

  const resolvedTheme = useMemo(() => {
    if (selectedTheme === ColorThemes.dark) {
      return themeFromConfigFile.dark_mode;
    } else {
      return themeFromConfigFile.light_mode;
    }
  }, [themeFromConfigFile, selectedTheme]);

  const configCSSColors = useMemo(
    () =>
      toCustomColorTheme({
        backgroundColor: resolvedTheme.background_color,
        brandColor: resolvedTheme.brand_color,
        hoverColor: resolvedTheme.hover_color,
        iconColor: resolvedTheme.icon_color,
        primaryColor,
        primaryFontColor: resolvedTheme.primary_font_color,
        secondaryColor: resolvedTheme.secondary_color,
        secondaryFontColor: resolvedTheme.secondary_font_color,
        warningColor: themeFromConfigFile.warning_color,
      }),
    [primaryColor, resolvedTheme, themeFromConfigFile],
  );

  const handleColorThemeCSS = useCallback(
    (useDarkTheme: boolean) => {
      const html = document.querySelector("html");
      if (useDarkTheme) {
        html?.setAttribute("data-color-theme", "dark-color-theme");
      } else {
        html?.removeAttribute("data-color-theme");
      }

      setCSSColorVariables({
        suffix: useDarkTheme ? "-dark" : undefined,
        theme: configCSSColors,
      });
    },
    [configCSSColors],
  );

  const handleAccessibleColorThemeCSS = useCallback(
    (colorTheme: AccessibleColorThemes) => {
      const theme =
        colorTheme === AccessibleColorThemes.highContrastDark
          ? DARK_ACCESSIBLE_THEME
          : LIGHT_ACCESSIBLE_THEME;
      setCSSColorVariables({ theme: toCustomColorTheme(theme) });

      const themeId = colorTheme.split(" ").join("-").toLowerCase();
      document.querySelector("html")?.setAttribute("data-color-theme", themeId);
    },
    [],
  );

  // Respond to system color-scheme changes
  useEffect(() => {
    if (colorTheme !== ColorThemes.system) return;

    const listener = () => {
      dispatch(setSelectedTheme(resolveSystemTheme(hostColorSchemePreference)));
    };

    const mediaQuery = getThemeMediaQueryList();
    if (!mediaQuery) {
      return;
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", listener);
    } else {
      mediaQuery.addListener(listener);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    };
  }, [colorTheme, hostColorSchemePreference, dispatch]);

  // Update selected theme based on configuration
  useEffect(() => {
    if (colorTheme === ColorThemes.system) {
      dispatch(setSelectedTheme(resolveSystemTheme(hostColorSchemePreference)));
      return;
    }

    dispatch(setSelectedTheme(colorTheme));
  }, [colorTheme, dispatch, hostColorSchemePreference]);

  // Update CSS variables whenever color changes
  useEffect(() => {
    if (isThemeAccessible) {
      handleAccessibleColorThemeCSS(colorTheme as AccessibleColorThemes);
      return;
    }

    handleColorThemeCSS(selectedTheme === ColorThemes.dark);
  }, [
    colorTheme,
    configCSSColors,
    handleAccessibleColorThemeCSS,
    handleColorThemeCSS,
    selectedTheme,
    isThemeAccessible,
  ]);

  // Update Redux theme whenever configured color theme changes
  useEffect(() => {
    if (isThemeAccessible) {
      if (colorTheme === AccessibleColorThemes.highContrastDark) {
        dispatch(setDarkAccessibleTheme());
      } else {
        dispatch(setLightAccessibleTheme());
      }
      return;
    }

    dispatch(
      setTheme({
        accentLabels: labelsConfig,
        backgroundColor: resolvedTheme.background_color,
        brandColor: resolvedTheme.brand_color,
        iconColor: resolvedTheme.icon_color,
        labels: labelsConfig,
        pois: {
          borderColor: selectedPoiOutlineColor,
        },
        position: {
          accuracyColor: themeFromConfigFile.positioning_accuracy_color,
          dotColor: themeFromConfigFile.positioning_dot_color,
          iconColor: themeFromConfigFile.positioning_icon_color,
          outdoorDotColor: themeFromConfigFile.positioning_outdoor_dot_color,
        },
        primaryColor: primaryColor,
        primaryFontColor: resolvedTheme.primary_font_color,
        route: {
          arrowColor: pathArrowColor,
          indications: DEFAULT_THEME.route.indications,
          pathAnimationColor,
          pathColor: pathColor ? pathColor : primaryColor,
          pathDotGap,
          pathDotSize,
          pathUnselectedColor,
          showArrow: showStepArrow,
        },
        secondaryColor: resolvedTheme.secondary_color,
        secondaryFontColor: resolvedTheme.secondary_font_color,
        warningColor: themeFromConfigFile.warning_color,
      }),
    );
  }, [
    colorTheme,
    primaryColor,
    themeFromConfigFile,
    labelsConfig,
    resolvedTheme,
    pathColor,
    pathArrowColor,
    showStepArrow,
    pathAnimationColor,
    selectedPoiOutlineColor,
    pathUnselectedColor,
    pathDotSize,
    pathDotGap,
    isThemeAccessible,
  ]);

  // Update radius theme
  useEffect(() => {
    document
      .querySelector("html")
      ?.setAttribute("data-radius-theme", styleBorderTheme.toLowerCase());
  }, [styleBorderTheme]);

  // Change buildings IMDF theme when the theme changes
  useEffect(() => {
    if (!buildingGeoJSON) return;
    if (selectedTheme === AccessibleColorThemes.highContrastDark) {
      buildingGeoJSON?.changeTheme(AccessibleColorThemes.highContrastDark);
    } else {
      buildingGeoJSON?.changeTheme(ColorThemes.light);
    }
  }, [buildingGeoJSON, selectedTheme]);
}

export default useTheme;
